import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../common/contexts/ProjectContext";

import { CustomDialog } from "../../common/components/CustomDialog";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import "../views/Discovery.css"; // Import for filter-tag classes
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";
import { ConsultaPdfExportModal } from "./ConsultaPdfExportModal";
import { useConsultaPdfExport } from "../hooks/useConsultaPdfExport";

const API_BASE = config.API_URL;

// Citation type from full-context-service
interface Citation {
  id: string;
  srcKey: string; // e.g. 'LOG-42' or 'TRANS-189'
  type: string; // 'log' | 'trans' | 'TRANS' | 'OTF' etc
  role?: string; // 'transcription' | 'otf' etc
  fullId: string;
  tc: string;
  date?: string | null;
  text: string;
  storyId?: string | null;
  speaker?: string | null;
  endAt?: string | null;
}

interface BrainContextPanelProps {
  onNavigateToLog?: (
    logId: string,
    storyId: string,
    highlightText?: string,
    citationType?: string,
    citationTc?: string,
  ) => void;
  onCitationNavigate?: (citation: Citation) => void;
  activeFilters?: any;
  dataSources?: { logs: boolean; transcripts: boolean; otfs?: boolean };
  sessionId?: string | null;
  chatHistory?: ChatMessage[];
  onChatHistoryChange?: (history: ChatMessage[]) => void;
  onEnsureSession?: () => Promise<string | null>;
  disabled?: boolean;
  hideAiDetails?: boolean;
  mode?: "general" | "vtr";
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  metadata?: {
    model?: string;
    contextSize?: number;
    estimatedTokens?: number;
    inputTokens?: number;
    outputTokens?: number;
    cost?: number;
    responseTimeMs?: number;
    filtered?: boolean;
    components?: {
      rundowns: number;
      events: number;
      stories: number;
      logs: number;
      transcriptions: number;
    };
    usedFilters?: {
      selectAll: boolean;
      rundownNames: string[];
      eventNames: string[];
      storyNames: string[];
      participantNames: string[];
      tagNames: string[];
      tagTypes: string[];
      sourceType: string;
    };
    searchPipeline?: {
      mode: string;
      totalStoriesAvailable: number;
      storiesUsed: number;
      selectorUsed: boolean;
      selectorModel?: string | null;
      generatorModel?: string;
      sourceType: string;
      contextSizeKB: number;
      summary: string;
    };
  };
  suggestedQuestions?: string[];
}

export const BrainContextPanel: React.FC<BrainContextPanelProps> = ({
  onNavigateToLog,
  onCitationNavigate,
  activeFilters,
  dataSources,
  sessionId,
  chatHistory: externalHistory,
  onChatHistoryChange,
  onEnsureSession,
  disabled,
  hideAiDetails = false,
  mode = "general",
}) => {
  const { t } = useTranslation();
  const { activeProject, participantList } = useProjects();
  const [query, setQuery] = useState("");
  const [globalAlert, setGlobalAlert] = useState<{
    isOpen: boolean;
    message: string;
    title?: string;
  } | null>(null);
  const [internalHistory, setInternalHistory] = useState<ChatMessage[]>([]);
  const chatHistory = externalHistory ?? internalHistory;
  const setChatHistory = onChatHistoryChange ?? setInternalHistory;
  const [isLoading, setIsLoading] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [rotatingMessageIndex, setRotatingMessageIndex] = useState(0);
  const rotatingMessages = React.useMemo(
    () => [
      t("discovery.rotating.thinking", "Pensando…"),
      t("discovery.rotating.analyzing", "Analizando el contexto…"),
      t("discovery.rotating.searching", "Buscando información relevante…"),
      t("discovery.rotating.summarizing", "Resumiendo…"),
      t("discovery.rotating.finalizing", "Casi listo…"),
    ],
    [t],
  );
  useEffect(() => {
    if (!hideAiDetails || !isLoading) {
      setRotatingMessageIndex(0);
      return;
    }
    const id = setInterval(() => {
      setRotatingMessageIndex((i) => (i + 1) % rotatingMessages.length);
    }, 2500);
    return () => clearInterval(id);
  }, [hideAiDetails, isLoading, rotatingMessages.length]);
  // Derive sourceType from dataSources prop (from Discovery toggles)
  // For backward compatibility, map to sourceType but also pass full dataSources
  const sourceType: "both" | "segment" | "log" = dataSources
    ? dataSources.logs && (dataSources.transcripts || dataSources.otfs)
      ? "both"
      : dataSources.transcripts || dataSources.otfs
        ? "segment"
        : dataSources.logs
          ? "log"
          : "both"
    : "both";
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingMsgIndex, setSpeakingMsgIndex] = useState<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const [activeCitation, setActiveCitation] = useState<{
    citation: Citation;
    x: number;
    y: number;
  } | null>(null);
  const [expandedMetadata, setExpandedMetadata] = useState<Set<number>>(new Set());
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const { exportMessage: exportMessageToPdf, exporting: messagePdfExporting } =
    useConsultaPdfExport(sessionId ?? null);
  const [exportingMessageIndex, setExportingMessageIndex] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Strip citation markers ([LOG-N]/[TRANS-N]/[OTF-N], grouped variants, stray
  // timecodes) from an assistant message so the .txt export reads as plain prose.
  const stripCitationMarkers = (content: string): string => {
    if (!content) return "";
    return content
      .replace(/\[([^\]]*?)(LOG-\d+)\]/g, "[$2]")
      .replace(/\[([^\]]*?)(TRANS-\d+)\]/g, "[$2]")
      .replace(/\[([^\]]*?)(OTF-\d+)\]/g, "[$2]")
      .replace(/\s*\[\d{1,2}:\d{2}(?::\d{2})?\]/g, "")
      .replace(/\s*\[(?:LOG|TRANS|OTF)-\d+(?:,\s*(?:LOG|TRANS|OTF)-\d+)*\]/g, "")
      .replace(/[ \t]+([.,;:!?])/g, "$1")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  };

  const handleExportTxt = () => {
    const summary = chatHistory
      .filter((m) => m.role === "assistant")
      .map((m) => stripCitationMarkers(m.content))
      .filter((s) => s.length > 0)
      .join("\n\n---\n\n");
    if (!summary) return;

    const blob = new Blob([summary], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    const slug = sessionId ? sessionId.slice(0, 8) : "consulta";
    a.href = url;
    a.download = `resumen-${slug}-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isLoading]);

  const handleSearch = async (directQuery?: string) => {
    const searchText = directQuery || query;
    if (!searchText.trim() || !activeProject?.id) return;

    // Capture projectId immediately — activeProject is reactive state and could
    // change during the async operations below (session negotiation, fetch, etc.)
    const projectId = activeProject.id;

    const userMsg: ChatMessage = { role: "user", content: searchText };
    setChatHistory([...chatHistory, userMsg]);
    setQuery("");
    setIsLoading(true);
    setStreamingText("");
    setStatusMessage("Connecting...");

    // Create abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Ensure we have a session before sending
      let currentSessionId = sessionId;
      if (!currentSessionId && onEnsureSession) {
        currentSessionId = await onEnsureSession();
      }

      // Get auth token for Brain API security
      const { supabase } = await import("../../../services/supabase");
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      // Step 1: POST query data to prepare endpoint, get a streamId
      // VTR mode targets a dedicated pipeline that ignores logs/trans/OTF
      // filters and uses the whole VTR/cortes catalog instead.
      const prepareUrl =
        mode === "vtr"
          ? `${API_BASE}/api/chat/brain/prepare-vtr`
          : `${API_BASE}/api/chat/brain/prepare`;
      const prepareRes = await authFetch(prepareUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          query: userMsg.content,
          projectId,
          ...(mode === "general"
            ? {
                filters: {
                  ...activeFilters,
                  sourceType,
                  dataSources: dataSources || { logs: true, transcripts: true, otfs: true },
                },
              }
            : {}),
          history: chatHistory.map((m) => ({ role: m.role, content: m.content })),
          sessionId: currentSessionId || undefined,
        }),
        signal: controller.signal,
      });

      if (!prepareRes.ok) {
        const errData = await prepareRes.json().catch(() => ({}));
        if (prepareRes.status === 401) {
          throw new Error(errData.error || "Sesión expirada. Recarga la página.");
        }
        if (prepareRes.status === 429) {
          throw new Error(errData.error || "Demasiadas consultas. Espera un momento.");
        }
        throw new Error(`Prepare error: ${prepareRes.status}`);
      }

      const { streamId } = await prepareRes.json();
      console.debug("[Brain SSE] Got streamId:", streamId);

      // Step 2: Connect via native EventSource (GET)
      const eventsUrl =
        mode === "vtr"
          ? `${API_BASE}/api/chat/brain/events-vtr/${streamId}`
          : `${API_BASE}/api/chat/brain/events/${streamId}`;
      const eventSource = new EventSource(eventsUrl);
      let accumulated = "";
      let finalData: any = null;
      let streamError: string | null = null;

      await new Promise<void>((resolve, reject) => {
        // Abort handler
        const onAbort = () => {
          console.debug("[Brain SSE] Abort signal received");
          eventSource.close();
          reject(new DOMException("Aborted", "AbortError"));
        };
        controller.signal.addEventListener("abort", onAbort);
        eventSource.addEventListener("status", (e: MessageEvent) => {
          try {
            const data = JSON.parse(e.data);
            console.debug("[Brain SSE] Status:", data.message);
            setStatusMessage(data.message);
          } catch {
            /* ignore parse errors */
          }
        });

        eventSource.addEventListener("clear", () => {
          // Backend is retrying (e.g. after 429) — clear accumulated text
          accumulated = "";
          setStreamingText("");
        });

        eventSource.addEventListener("chunk", (e: MessageEvent) => {
          try {
            const data = JSON.parse(e.data);
            accumulated += data.text;
            // Remove [SUGERENCIAS] block from visible streaming text
            const visibleText = accumulated
              .replace(/\[SUGERENCIAS\][\s\S]*?\[\/SUGERENCIAS\]/g, "")
              .replace(/\[SUGERENCIAS\][\s\S]*$/g, "")
              .trim();
            setStreamingText(visibleText);
            setStatusMessage("");
          } catch {
            /* ignore parse errors */
          }
        });

        eventSource.addEventListener("done", (e: MessageEvent) => {
          try {
            finalData = JSON.parse(e.data);
            console.debug(
              "[Brain SSE] Done received, fullText length:",
              finalData.fullText?.length,
            );
          } catch {
            /* ignore parse errors */
          }
        });

        eventSource.addEventListener("error", (e: any) => {
          // Check if it's a named error event with data
          if (e.data) {
            try {
              const data = JSON.parse(e.data);
              // Fall back through message → error → generic so a payload that
              // parses but lacks `message` doesn't degrade to "Connection lost".
              streamError = data.message || data.error || "Error del servidor";
              setStatusMessage(`Error: ${streamError}`);
            } catch {
              /* ignore */
            }
          }
          eventSource.close();
          controller.signal.removeEventListener("abort", onAbort);
          if (streamError) {
            reject(new Error(streamError));
          } else if (finalData) {
            resolve();
          } else {
            // EventSource error (connection closed) — treat as completion if we have data
            if (accumulated) {
              resolve();
            } else {
              reject(new Error("Connection lost"));
            }
          }
        });

        // EventSource doesn't have a native "close" event,
        // the server closes the connection which triggers 'error'
        // But we also listen for 'done' + a small delay to resolve
        eventSource.addEventListener("done", () => {
          setTimeout(() => {
            eventSource.close();
            controller.signal.removeEventListener("abort", onAbort);
            resolve();
          }, 500);
        });
      });

      // Build final assistant message
      if (finalData) {
        const assistantMsg: ChatMessage = {
          role: "assistant",
          content: finalData.fullText,
          citations: finalData.citations,
          metadata: {
            ...finalData.metadata,
            usedFilters: {
              selectAll: activeFilters?.selectAll || false,
              rundownNames: activeFilters?.rundownNames || [],
              eventNames: activeFilters?.eventNames || [],
              storyNames: activeFilters?.storyNames || [],
              participantNames: activeFilters?.participantNames || [],
              tagNames: activeFilters?.tagNames || [],
              tagTypes: activeFilters?.tagTypes || [],
              sourceType,
            },
          },
          suggestedQuestions: finalData.suggestedQuestions || [],
        };
        setChatHistory([...chatHistory, userMsg, assistantMsg]);

        // ── Persist messages to Supabase ──
        if (currentSessionId) {
          authFetch(`/api/chat/sessions/${currentSessionId}/messages`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: [
                { role: "user", content: userMsg.content },
                {
                  role: "assistant",
                  content: finalData.fullText,
                  citations: finalData.citations,
                  metadata: assistantMsg.metadata,
                  suggestedQuestions: assistantMsg.suggestedQuestions,
                },
              ],
            }),
          }).catch((err) => console.error("[Chat] Error persisting messages:", err));
        }
      } else if (accumulated) {
        // Fallback: use accumulated text
        setChatHistory([
          ...chatHistory,
          userMsg,
          {
            role: "assistant",
            content: accumulated,
          },
        ]);
        // Persist fallback too
        if (currentSessionId) {
          authFetch(`/api/chat/sessions/${currentSessionId}/messages`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: [
                { role: "user", content: userMsg.content },
                { role: "assistant", content: accumulated },
              ],
            }),
          }).catch((err) => console.error("[Chat] Error persisting messages:", err));
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        // User cancelled — remove the pending user message
        setChatHistory([...chatHistory]);
      } else {
        console.error(error);
        setChatHistory([
          ...chatHistory,
          userMsg,
          {
            role: "assistant",
            content: `Error connecting to Brain: ${error instanceof Error ? error.message : "Unknown error"}`,
          },
        ]);
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
      setStreamingText("");
      setStatusMessage("");
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // ─── Speech-to-Text (Microphone) ─────────────────────────
  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setGlobalAlert({
        isOpen: true,
        title: t("discovery.attention"),
        message: t("discovery.speechNotSupported"),
        type: "alert",
      } as any);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "es-ES"; // Spanish for the production team
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    let finalTranscript = query; // Append to existing text

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? " " : "") + transcript;
        } else {
          interim = transcript;
        }
      }
      setQuery(finalTranscript + (interim ? " " + interim : ""));
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  // ─── Text-to-Speech (Read Aloud via Edge Neural Voice) ─────
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const speakText = async (text: string, msgIndex: number) => {
    // Stop any current playback
    stopSpeaking();

    setIsSpeaking(true);
    setSpeakingMsgIndex(msgIndex);

    try {
      const response = await authFetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) throw new Error("TTS failed");

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onended = () => {
        setIsSpeaking(false);
        setSpeakingMsgIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setSpeakingMsgIndex(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audioRef.current = audio;
      await audio.play();
    } catch (err) {
      console.error("TTS error:", err);
      setIsSpeaking(false);
      setSpeakingMsgIndex(null);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel(); // Clean up any legacy
    setIsSpeaking(false);
    setSpeakingMsgIndex(null);
  };

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      window.speechSynthesis.cancel();
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && query.trim() !== "") {
        handleSearch();
        // Reset textarea height after sending
        setTimeout(() => {
          const target = e.target as HTMLTextAreaElement;
          if (target) {
            target.style.height = "auto"; // Back to rows=1 default
          }
        }, 10);
      }
    }
  };

  // Function to render message content with inline citation badges and markdown
  const renderContentWithCitations = (content: string, citations: Citation[] | undefined) => {
    const citationList = citations || [];

    // Build lookup map: 'LOG-42' -> Citation AND 'LOG-42' -> Sequential Number (1, 2, 3...)
    const citationMap = new Map<string, Citation>();
    const sequentialMap = new Map<string, number>();

    citationList.forEach((c, index) => {
      if (c.srcKey) {
        citationMap.set(c.srcKey, c);
        // Only assign number if not already assigned (to handle duplicates if any)
        if (!sequentialMap.has(c.srcKey)) {
          sequentialMap.set(c.srcKey, index + 1);
        }
      }
    });

    // Parse content into lines for markdown structure
    const lines = content.split("\n");
    const elements: React.ReactNode[] = [];

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        elements.push(<br key={`br-${lineIdx}`} />);
        return;
      }

      // Detect markdown headings: ### Heading or ## Heading
      const h3Match = trimmed.match(/^###\s+(.+)$/);
      if (h3Match) {
        elements.push(
          <h3
            key={`h3-${lineIdx}`}
            style={{
              color: "var(--discovery-accent)",
              margin: "20px 0 10px 0",
              fontSize: "1rem",
              fontWeight: 700,
              borderBottom: "1px solid rgba(255,77,141,0.3)",
              paddingBottom: "6px",
              letterSpacing: "0.02em",
            }}
          >
            {h3Match[1]}
          </h3>,
        );
        return;
      }

      const h2Match = trimmed.match(/^##\s+(.+)$/);
      if (h2Match) {
        elements.push(
          <h2
            key={`h2-${lineIdx}`}
            style={{
              color: "var(--discovery-accent)",
              margin: "24px 0 12px 0",
              fontSize: "1.1rem",
              fontWeight: 700,
              borderBottom: "2px solid rgba(255,77,141,0.4)",
              paddingBottom: "8px",
            }}
          >
            {h2Match[1]}
          </h2>,
        );
        return;
      }

      // Detect category headers: "**• Categoría: Título**" or "**• Título**"
      const headerMatch = trimmed.match(/^\*\*•\s*(.+?)\*\*$/);
      if (headerMatch) {
        elements.push(
          <h4
            key={`h-${lineIdx}`}
            style={{
              color: "var(--discovery-accent)",
              margin: "16px 0 8px 0",
              fontSize: "0.9rem",
              fontWeight: 600,
              borderBottom: "1px solid rgba(255,77,141,0.2)",
              paddingBottom: "4px",
            }}
          >
            {headerMatch[1]}
          </h4>,
        );
        return;
      }

      // Detect bullet points: starts with ○, ■, ●, or "- " or "* " (with space to avoid eating bold **)
      const bulletMatch = trimmed.match(/^([○■●]|\*\s+|-\s+)\s*(.+)$/);
      const isBullet = bulletMatch !== null;
      const lineContent = isBullet ? bulletMatch![2] : trimmed;

      // Parse inline elements: **bold**, [LOG-N], [TRANS-N], «dialogue»
      const inlineParts: React.ReactNode[] = [];
      // Pre-process: normalize emoji-prefixed citations [📋 LOG-228] → [LOG-228]
      let processedContent = lineContent
        .replace(/\[([^\]]*?)(LOG-\d+)\]/g, "[$2]")
        .replace(/\[([^\]]*?)(TRANS-\d+)\]/g, "[$2]");
      // Pre-process: strip residual timecodes [HH:MM:SS] or [MM:SS] that the AI may include
      processedContent = processedContent.replace(/\s*\[\d{1,2}:\d{2}(?::\d{2})?\]/g, "");
      // Pre-process: expand grouped citations [TRANS-251, TRANS-252] → [TRANS-251][TRANS-252]
      processedContent = processedContent.replace(/\[((?:(?:LOG|TRANS)-\d+,?\s*)+)\]/g, (match) => {
        const inner = match.slice(1, -1); // Remove outer []
        const parts = inner.split(/,\s*/);
        return parts.map((p) => `[${p.trim()}]`).join("");
      });
      // Pre-process: clean orphaned asterisks that don't form valid bold/italic pairs
      // First protect valid bold **text** and italic *text*, then strip remaining *
      processedContent = processedContent
        .replace(/\*\*(.+?)\*\*/g, "⟦BOLD⟧$1⟦/BOLD⟧") // protect bold
        .replace(/\*([^*\s][^*]*?)\*/g, "⟦ITALIC⟧$1⟦/ITALIC⟧") // protect italic (must not start with space)
        .replace(/\*/g, "") // strip orphaned asterisks
        .replace(/⟦BOLD⟧/g, "**")
        .replace(/⟦\/BOLD⟧/g, "**") // restore bold
        .replace(/⟦ITALIC⟧/g, "*")
        .replace(/⟦\/ITALIC⟧/g, "*"); // restore italic

      // Match **bold**, *italic*, [LOG-N], [TRANS-N], «dialogue»
      const inlinePattern = /(\*\*(.+?)\*\*|\*([^*\s][^*]*?)\*|\[(LOG|TRANS)-(\d+)\]|«(.+?)»)/g;
      let lastIdx = 0;
      let inlineMatch;

      while ((inlineMatch = inlinePattern.exec(processedContent)) !== null) {
        // Text before match
        if (inlineMatch.index > lastIdx) {
          inlineParts.push(
            <span key={`t-${lineIdx}-${lastIdx}`}>
              {processedContent.slice(lastIdx, inlineMatch.index)}
            </span>,
          );
        }

        if (inlineMatch[6]) {
          // «dialogue» match - render as styled quote
          inlineParts.push(
            <span
              key={`q-${lineIdx}-${inlineMatch.index}`}
              style={{
                fontStyle: "italic",
                color: "#d4d4d4",
                background: "rgba(255,77,141,0.08)",
                padding: "1px 4px",
                borderRadius: "3px",
                borderLeft: "2px solid var(--discovery-accent)",
              }}
            >
              «{inlineMatch[6]}»
            </span>,
          );
        } else if (inlineMatch[2]) {
          // **bold** match
          inlineParts.push(
            <strong key={`b-${lineIdx}-${inlineMatch.index}`} style={{ color: "#eee" }}>
              {inlineMatch[2]}
            </strong>,
          );
        } else if (inlineMatch[3]) {
          // *italic* match
          inlineParts.push(
            <em key={`i-${lineIdx}-${inlineMatch.index}`} style={{ color: "#ccc" }}>
              {inlineMatch[3]}
            </em>,
          );
        } else if (inlineMatch[4] && inlineMatch[5]) {
          // [LOG-N] or [TRANS-N] citation
          const citKey = `${inlineMatch[4]}-${inlineMatch[5]}`;
          const cit = citationMap.get(citKey);
          const isLog = inlineMatch[4] === "LOG";

          const seqNum = sequentialMap.get(citKey);

          // If citation doesn't exist in our map, still show as a styled badge (non-navigable)
          if (!seqNum) {
            inlineParts.push(
              <sup
                key={`cit-ghost-${lineIdx}-${inlineMatch.index}`}
                title={`${citKey} — Referencia del modelo sin enlace directo`}
                style={{
                  background: isLog ? "rgba(255,77,141,0.25)" : "rgba(139,92,246,0.25)",
                  color: isLog ? "rgba(255,77,141,0.7)" : "rgba(139,92,246,0.7)",
                  padding: "1px 5px",
                  borderRadius: "6px",
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  marginLeft: "1px",
                  marginRight: "1px",
                  display: "inline-block",
                  lineHeight: "1.2",
                  cursor: "default",
                }}
              >
                {citKey}
              </sup>,
            );
            lastIdx = inlineMatch.index + inlineMatch[0].length;
            continue;
          }

          const tcHasTime =
            cit?.tc && cit.tc !== "N/A" && cit.tc !== "TRANS" && cit.tc !== "00:00:00";
          const tcLabel = cit
            ? cit.date
              ? tcHasTime
                ? `${cit.date} ${cit.tc}`
                : cit.date
              : cit.tc
            : "";
          const tooltipText = cit
            ? isLog
              ? `Log: ${cit.speaker ? `👤 ${cit.speaker} ` : ""}[${tcLabel}] — ${cit.text?.substring(0, 100)}`
              : `Transcript: ${cit.speaker || "Audio"} [${tcLabel}] — ${cit.text?.substring(0, 100)}`
            : citKey;

          const isOtf = cit && (cit.type === "OTF" || cit.role === "otf");
          const bgColor = isOtf ? "#06b6d4" : isLog ? "var(--color-data-log)" : "#8B5CF6";

          inlineParts.push(
            <sup
              key={`cit-${lineIdx}-${inlineMatch.index}`}
              onClick={(e) => {
                if (cit && cit.storyId && onNavigateToLog) {
                  // LOGs and TRANS with valid storyId: Navigate to Explorer
                  onNavigateToLog(cit.id, cit.storyId, cit.text, cit.type, cit.tc);
                } else if (cit && onCitationNavigate) {
                  // Fallback: use citation navigate handler
                  onCitationNavigate(cit);
                } else if (cit) {
                  // Last resort: show popup with details
                  const rect = (e.target as HTMLElement).getBoundingClientRect();
                  setActiveCitation({
                    citation: cit,
                    x: rect.left,
                    y: rect.top - 10,
                  });
                }
              }}
              title={tooltipText}
              style={{
                background: bgColor,
                color: "#fff",
                padding: "1px 6px",
                borderRadius: "6px",
                fontSize: "0.65rem",
                fontWeight: 700,
                cursor: cit ? "pointer" : "default",
                marginLeft: "2px",
                marginRight: "2px",
                userSelect: "none" as const,
                transition: "all 0.2s ease",
                opacity: cit ? 1 : 0.6,
                whiteSpace: "nowrap" as const,
                minWidth: "18px",
                textAlign: "center",
                display: "inline-block",
                lineHeight: "1.2",
              }}
              onMouseOver={(e) => {
                (e.target as HTMLElement).style.transform = "scale(1.15)";
                (e.target as HTMLElement).style.boxShadow = `0 0 8px ${bgColor}`;
              }}
              onMouseOut={(e) => {
                (e.target as HTMLElement).style.transform = "scale(1)";
                (e.target as HTMLElement).style.boxShadow = "none";
              }}
            >
              {seqNum}
            </sup>,
          );
        }

        lastIdx = inlineMatch.index + inlineMatch[0].length;
      }

      // Remaining text
      if (lastIdx < processedContent.length) {
        inlineParts.push(<span key={`t-${lineIdx}-end`}>{processedContent.slice(lastIdx)}</span>);
      }

      // Wrap in appropriate element
      if (isBullet) {
        elements.push(
          <div
            key={`line-${lineIdx}`}
            style={{
              paddingLeft: "16px",
              margin: "4px 0",
              lineHeight: "1.6",
              position: "relative",
            }}
          >
            <span style={{ position: "absolute", left: "0", color: "var(--discovery-accent)" }}>
              •
            </span>
            {inlineParts}
          </div>,
        );
      } else {
        elements.push(
          <div key={`line-${lineIdx}`} style={{ margin: "4px 0", lineHeight: "1.6" }}>
            {inlineParts}
          </div>,
        );
      }
    });

    return <div style={{ fontSize: "0.85rem" }}>{elements}</div>;
  };

  return (
    <div
      className="discovery-main"
      data-testid="brain-context-panel"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      onClick={() => activeCitation && setActiveCitation(null)}
    >
      {/* Citation Source Popup */}
      {activeCitation &&
        (() => {
          const isTrans =
            activeCitation.citation.role === "transcription" ||
            activeCitation.citation.type === "TRANS";
          const isOtf =
            activeCitation.citation.role === "otf" || activeCitation.citation.type === "OTF";

          // Determine theme color based on type
          let themeColor = "#ff4d8d"; // Default LOG pink
          let bgThemeColor = "rgba(255, 77, 141, 0.08)";
          let titleText = "RECORDING LOG";
          let iconClass = "bx bx-file";

          if (isOtf) {
            themeColor = "#06b6d4"; // Cyan for OTF
            bgThemeColor = "rgba(6, 182, 212, 0.08)";
            titleText = t("discovery.interview");
            iconClass = "bx bx-camera-movie";
          } else if (isTrans) {
            themeColor = "#8B5CF6"; // Purple for TRANS
            bgThemeColor = "rgba(139, 92, 246, 0.08)";
            titleText = t("discovery.transcript");
            iconClass = "bx bx-microphone";
          }

          return (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                left: Math.min(activeCitation.x, window.innerWidth - 340),
                top: Math.max(activeCitation.y - 160, 10),
                width: "320px",
                background: "rgba(30, 30, 40, 0.95)",
                backdropFilter: "blur(16px)",
                border: `1px solid ${themeColor}66`, // 40% opacity hex
                borderRadius: "12px",
                padding: "16px",
                zIndex: 9999,
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 12px ${themeColor}33`,
                animation: "fadeIn 0.15s ease-out",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: themeColor,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                  }}
                >
                  <i className={iconClass} style={{ marginRight: "4px" }}></i> {titleText}
                </span>
                <span
                  onClick={() => setActiveCitation(null)}
                  style={{ cursor: "pointer", color: "#888", fontSize: "1rem", lineHeight: 1 }}
                >
                  ×
                </span>
              </div>
              {activeCitation.citation.speaker && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#eee",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                >
                  {activeCitation.citation.speaker}
                </div>
              )}
              <div style={{ fontSize: "0.7rem", color: "#888", marginBottom: "8px" }}>
                <i className="bx bx-time" style={{ marginRight: "4px" }}></i>
                {activeCitation.citation.tc || "N/A"}
                {activeCitation.citation.endAt && (
                  <span>
                    {" "}
                    →{" "}
                    {new Date(activeCitation.citation.endAt).toLocaleTimeString("en-GB", {
                      timeZone: "America/Mexico_City",
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#ccc",
                  lineHeight: "1.5",
                  fontStyle: "italic",
                  maxHeight: "120px",
                  overflowY: "auto",
                  background: bgThemeColor,
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                "{activeCitation.citation.text}"
              </div>
              <div
                style={{ fontSize: "0.65rem", color: "#555", marginTop: "8px", textAlign: "right" }}
              >
                {activeCitation.citation.srcKey}
              </div>
            </div>
          );
        })()}
      {chatHistory.length > 0 && (
        <div
          className="brain-chat-toolbar"
          data-testid="brain-chat-toolbar"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "8px",
            padding: "8px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <button
            onClick={handleExportTxt}
            disabled={!chatHistory.some((m) => m.role === "assistant")}
            data-testid="brain-export-txt-button"
            title={
              t(
                "consultaPdf.exportTxt",
                "Exportar solo el resumen (respuestas del Brain) como .txt",
              ) as string
            }
            style={{
              background: "transparent",
              border: "1px solid #333",
              borderRadius: "6px",
              padding: "4px 10px",
              color: chatHistory.some((m) => m.role === "assistant") ? "#ccc" : "#555",
              fontSize: "0.72rem",
              cursor: chatHistory.some((m) => m.role === "assistant") ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!chatHistory.some((m) => m.role === "assistant")) return;
              (e.currentTarget as HTMLElement).style.borderColor = "var(--discovery-accent)";
              (e.currentTarget as HTMLElement).style.color = "var(--discovery-accent)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "#333";
              (e.currentTarget as HTMLElement).style.color = chatHistory.some(
                (m) => m.role === "assistant",
              )
                ? "#ccc"
                : "#555";
            }}
          >
            <i className="bx bx-file" style={{ fontSize: "0.9rem" }}></i>
            {t("consultaPdf.buttonTxt", "Export TXT")}
          </button>
          <button
            onClick={() => setPdfExportOpen(true)}
            disabled={!sessionId}
            data-testid="brain-export-pdf-button"
            title={t("consultaPdf.exportSession", "Exportar sesión completa") as string}
            style={{
              background: "transparent",
              border: "1px solid #333",
              borderRadius: "6px",
              padding: "4px 10px",
              color: sessionId ? "#ccc" : "#555",
              fontSize: "0.72rem",
              cursor: sessionId ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!sessionId) return;
              (e.currentTarget as HTMLElement).style.borderColor = "var(--discovery-accent)";
              (e.currentTarget as HTMLElement).style.color = "var(--discovery-accent)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "#333";
              (e.currentTarget as HTMLElement).style.color = sessionId ? "#ccc" : "#555";
            }}
          >
            <i className="bx bx-file-pdf" style={{ fontSize: "0.9rem" }}></i>
            {t("consultaPdf.button", "Exportar PDF")}
          </button>
        </div>
      )}
      <ConsultaPdfExportModal
        open={pdfExportOpen}
        sessionId={sessionId ?? null}
        onClose={() => setPdfExportOpen(false)}
      />
      <div
        className="chat-history"
        data-testid="brain-chat-history"
        style={{ flex: 1, overflowY: "auto", padding: "20px" }}
      >
        {chatHistory.length === 0 && (
          <div className="chat-empty" data-testid="brain-chat-empty">
            <i
              className="bx bx-brain"
              style={{
                fontSize: "4rem",
                marginBottom: "20px",
                opacity: 0.1,
                color: "var(--discovery-accent)",
              }}
            ></i>
            <p>{t("brain.title")}</p>
            <p style={{ fontSize: "0.9rem", opacity: 0.7 }}>{t("brain.instruction")}</p>
          </div>
        )}

        {chatHistory.map((msg, i) => (
          <div
            key={i}
            className={`chat-message ${msg.role}`}
            data-testid={`brain-chat-message-${i}`}
          >
            <div className="message-content" data-testid={`brain-message-content-${i}`}>
              {msg.role === "assistant"
                ? renderContentWithCitations(msg.content, msg.citations)
                : msg.content}
            </div>
            {/* Context metadata indicator */}
            {msg.role === "assistant" && msg.metadata && !hideAiDetails && (
              <div style={{ marginTop: "8px" }}>
                {/* Compact metadata bar — always visible */}
                <div
                  onClick={() => {
                    setExpandedMetadata((prev) => {
                      const next = new Set(prev);
                      if (next.has(i)) {
                        next.delete(i);
                      } else {
                        next.add(i);
                      }
                      return next;
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 10px",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: expandedMetadata.has(i) ? "6px 6px 0 0" : "6px",
                    fontSize: "0.7rem",
                    color: "#555",
                    flexWrap: "wrap",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    userSelect: "none" as const,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)";
                  }}
                >
                  <i
                    className={
                      expandedMetadata.has(i) ? "bx bx-chevron-down" : "bx bx-chevron-right"
                    }
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--discovery-accent)",
                      transition: "transform 0.2s",
                    }}
                  />
                  <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <i className="bx bx-chip" style={{ fontSize: "0.8rem" }} />
                    {msg.metadata.inputTokens ? (
                      <>
                        {Math.round(msg.metadata.inputTokens / 1000)}K in ·{" "}
                        {Math.round((msg.metadata.outputTokens || 0) / 1000)}K out
                      </>
                    ) : (
                      <>~{Math.round((msg.metadata.estimatedTokens || 0) / 1000)}K tokens</>
                    )}
                  </span>
                  {msg.metadata.cost != null && msg.metadata.cost > 0 && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        color: "#4ecdc4",
                      }}
                    >
                      <i className="bx bx-dollar" style={{ fontSize: "0.8rem" }} />
                      {msg.metadata.cost < 0.01
                        ? `${(msg.metadata.cost * 100).toFixed(2)}¢`
                        : `$${msg.metadata.cost.toFixed(3)}`}
                    </span>
                  )}
                  {msg.metadata.filtered && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        color: "var(--discovery-accent)",
                        background: "rgba(255,77,141,0.1)",
                        padding: "1px 6px",
                        borderRadius: "4px",
                      }}
                    >
                      <i className="bx bx-filter-alt" style={{ fontSize: "0.8rem" }} />
                      Filtered
                    </span>
                  )}
                  {msg.metadata.components && (
                    <span style={{ opacity: 0.7 }}>
                      {msg.metadata.components.logs > 0 && `${msg.metadata.components.logs} logs`}
                      {msg.metadata.components.logs > 0 &&
                        msg.metadata.components.transcriptions > 0 &&
                        " · "}
                      {msg.metadata.components.transcriptions > 0 &&
                        `${msg.metadata.components.transcriptions} trans`}
                    </span>
                  )}
                  {msg.metadata.responseTimeMs && (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: "3px", opacity: 0.6 }}
                    >
                      <i className="bx bx-time-five" style={{ fontSize: "0.8rem" }} />
                      {msg.metadata.responseTimeMs >= 1000
                        ? `${(msg.metadata.responseTimeMs / 1000).toFixed(1)}s`
                        : `${msg.metadata.responseTimeMs}ms`}
                    </span>
                  )}
                </div>
                {/* Expanded details — search pipeline + filters */}
                {expandedMetadata.has(i) && (
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "0 0 6px 6px",
                      borderTop: "1px solid rgba(255,77,141,0.1)",
                      fontSize: "0.7rem",
                      color: "#666",
                      animation: "fadeIn 0.15s ease-out",
                    }}
                  >
                    {/* Search Pipeline Summary */}
                    {msg.metadata.searchPipeline && (
                      <div style={{ marginBottom: "8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            marginBottom: "6px",
                            color: "var(--discovery-accent)",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                          }}
                        >
                          <i className="bx bx-search-alt-2" style={{ fontSize: "0.85rem" }} />
                          Search Pipeline
                        </div>
                        {/* Pipeline summary as readable flow */}
                        <div
                          style={{
                            background: "rgba(255,77,141,0.04)",
                            borderRadius: "6px",
                            padding: "8px 10px",
                            borderLeft: "2px solid rgba(255,77,141,0.3)",
                            lineHeight: "1.7",
                            color: "#888",
                          }}
                        >
                          {msg.metadata.searchPipeline.summary.split(" → ").map((step, si) => (
                            <div
                              key={si}
                              style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}
                            >
                              <span
                                style={{
                                  color: "var(--discovery-accent)",
                                  fontWeight: 700,
                                  fontSize: "0.65rem",
                                  marginTop: "2px",
                                  flexShrink: 0,
                                }}
                              >
                                {si + 1}.
                              </span>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>
                        {/* Mode badge + stats */}
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "6px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontWeight: 600,
                              fontSize: "0.65rem",
                              background:
                                msg.metadata.searchPipeline.mode === "RAW"
                                  ? "rgba(78,205,196,0.12)"
                                  : msg.metadata.searchPipeline.mode === "HYBRID"
                                    ? "rgba(255,193,7,0.12)"
                                    : "rgba(139,92,246,0.12)",
                              color:
                                msg.metadata.searchPipeline.mode === "RAW"
                                  ? "#4ecdc4"
                                  : msg.metadata.searchPipeline.mode === "HYBRID"
                                    ? "#ffc107"
                                    : "#8B5CF6",
                            }}
                          >
                            {msg.metadata.searchPipeline.mode}
                          </span>
                          <span style={{ opacity: 0.7 }}>
                            {msg.metadata.searchPipeline.storiesUsed} stories ·{" "}
                            {msg.metadata.searchPipeline.contextSizeKB}KB context
                          </span>
                          {msg.metadata.searchPipeline.selectorUsed && (
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: "4px",
                                background: "rgba(255,193,7,0.1)",
                                color: "#ffc107",
                                fontSize: "0.65rem",
                              }}
                            >
                              Brain Selector ✓{" "}
                              {msg.metadata.searchPipeline.selectorModel &&
                                `(${msg.metadata.searchPipeline.selectorModel})`}
                            </span>
                          )}
                          {msg.metadata.searchPipeline.generatorModel && (
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: "4px",
                                background: "rgba(78,205,196,0.08)",
                                color: "#4ecdc4",
                                fontSize: "0.65rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <i className="bx bx-chip" style={{ fontSize: "0.7rem" }} />
                              {msg.metadata.searchPipeline.generatorModel}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Used Filters (moved inside expandable) */}
                    {msg.metadata.usedFilters && (
                      <div style={{ marginTop: "6px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            marginBottom: "4px",
                            color: "#888",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        >
                          <i
                            className="bx bx-filter-alt"
                            style={{ fontSize: "0.8rem", color: "var(--discovery-accent)" }}
                          />
                          Applied Filters
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          {msg.metadata.usedFilters.selectAll && (
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: "4px",
                                background: "rgba(255,77,141,0.1)",
                                color: "var(--discovery-accent)",
                              }}
                            >
                              ALL
                            </span>
                          )}
                          {msg.metadata.usedFilters.sourceType &&
                            msg.metadata.usedFilters.sourceType !== "both" && (
                              <span
                                style={{
                                  padding: "1px 6px",
                                  borderRadius: "4px",
                                  background:
                                    msg.metadata.usedFilters.sourceType === "log"
                                      ? "rgba(247,45,96,0.1)"
                                      : "rgba(168,85,247,0.1)",
                                  color:
                                    msg.metadata.usedFilters.sourceType === "log"
                                      ? "var(--color-data-log)"
                                      : "var(--color-data-trans)",
                                }}
                              >
                                {msg.metadata.usedFilters.sourceType === "log"
                                  ? t("discovery.logsOnly")
                                  : t("discovery.transOnly")}
                              </span>
                            )}
                          {msg.metadata.usedFilters.rundownNames?.map(
                            (name: string, idx: number) => (
                              <span
                                key={`rf-${idx}`}
                                className="filter-tag rundown active"
                                style={{ fontSize: "0.65rem", padding: "1px 5px" }}
                              >
                                {name}
                              </span>
                            ),
                          )}
                          {msg.metadata.usedFilters.eventNames?.map((name: string, idx: number) => (
                            <span
                              key={`ef-${idx}`}
                              className="filter-tag event active"
                              style={{ fontSize: "0.65rem", padding: "1px 5px" }}
                            >
                              {name}
                            </span>
                          ))}
                          {msg.metadata.usedFilters.storyNames?.map((name: string, idx: number) => (
                            <span
                              key={`sf-${idx}`}
                              className="filter-tag story active"
                              style={{ fontSize: "0.65rem", padding: "1px 5px" }}
                            >
                              {name}
                            </span>
                          ))}
                          {msg.metadata.usedFilters.participantNames?.map(
                            (name: string, idx: number) => {
                              const cleanName = name.split(",")[0].trim().toLowerCase();
                              const participant = activeProject
                                ? participantList?.find(
                                    (p: any) =>
                                      p.name.toLowerCase() === cleanName ||
                                      (p.alias && p.alias.toLowerCase() === cleanName),
                                  )
                                : null;

                              return participant ? (
                                <ParticipantBadge
                                  key={`pf-${idx}`}
                                  participant={participant}
                                  variant="pill"
                                  size="sm"
                                />
                              ) : (
                                <span
                                  key={`pf-${idx}`}
                                  className="filter-tag participant active"
                                  style={{ fontSize: "0.65rem", padding: "1px 5px" }}
                                >
                                  {name}
                                </span>
                              );
                            },
                          )}
                          {msg.metadata.usedFilters.tagNames?.map((tag: string, idx: number) => {
                            const isCamera =
                              msg.metadata?.usedFilters?.tagTypes?.[idx] === "camera";
                            return (
                              <span
                                key={`tf-${idx}`}
                                className={`filter-tag ${isCamera ? "camera" : "tag"} active`}
                                style={{ fontSize: "0.65rem", padding: "1px 5px" }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                          {!msg.metadata.usedFilters.selectAll &&
                            !msg.metadata.usedFilters.rundownNames?.length &&
                            !msg.metadata.usedFilters.eventNames?.length &&
                            !msg.metadata.usedFilters.storyNames?.length &&
                            !msg.metadata.usedFilters.participantNames?.length &&
                            !msg.metadata.usedFilters.tagNames?.length && (
                              <span style={{ opacity: 0.5 }}>No filters</span>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Text-to-Speech button */}
            {msg.role === "assistant" && msg.content && msg.content.length > 10 && (
              <button
                onClick={() =>
                  isSpeaking && speakingMsgIndex === i ? stopSpeaking() : speakText(msg.content, i)
                }
                title={isSpeaking && speakingMsgIndex === i ? "Stop reading" : "Read aloud"}
                style={{
                  background:
                    isSpeaking && speakingMsgIndex === i ? "rgba(255,77,141,0.15)" : "transparent",
                  border:
                    "1px solid " +
                    (isSpeaking && speakingMsgIndex === i ? "var(--discovery-accent)" : "#333"),
                  borderRadius: "6px",
                  padding: "4px 10px",
                  color: isSpeaking && speakingMsgIndex === i ? "var(--discovery-accent)" : "#666",
                  fontSize: "0.7rem",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "6px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--discovery-accent)";
                  (e.currentTarget as HTMLElement).style.color = "var(--discovery-accent)";
                }}
                onMouseLeave={(e) => {
                  if (!(isSpeaking && speakingMsgIndex === i)) {
                    (e.currentTarget as HTMLElement).style.borderColor = "#333";
                    (e.currentTarget as HTMLElement).style.color = "#666";
                  }
                }}
              >
                <i
                  className={
                    isSpeaking && speakingMsgIndex === i ? "bx bx-stop" : "bx bx-volume-full"
                  }
                  style={{ fontSize: "0.85rem" }}
                ></i>
                {isSpeaking && speakingMsgIndex === i ? "Stop" : "Read aloud"}
              </button>
            )}
            {msg.role === "assistant" && sessionId && (
              <button
                onClick={async () => {
                  setExportingMessageIndex(i);
                  await exportMessageToPdf(i);
                  setExportingMessageIndex(null);
                }}
                disabled={messagePdfExporting}
                title={t("consultaPdf.exportMessage", "Exportar esta respuesta") as string}
                data-testid={`brain-export-message-pdf-${i}`}
                style={{
                  background: "transparent",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  padding: "4px 10px",
                  color: "#666",
                  fontSize: "0.7rem",
                  cursor: messagePdfExporting ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  marginTop: "6px",
                  marginLeft: "6px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "var(--discovery-accent)";
                  (e.currentTarget as HTMLElement).style.color = "var(--discovery-accent)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = "#333";
                  (e.currentTarget as HTMLElement).style.color = "#666";
                }}
              >
                <i
                  className={
                    exportingMessageIndex === i && messagePdfExporting
                      ? "bx bx-loader-alt bx-spin"
                      : "bx bx-file-pdf"
                  }
                  style={{ fontSize: "0.85rem" }}
                ></i>
                {t("consultaPdf.buttonShort", "PDF")}
              </button>
            )}
            {/* Suggested follow-up questions */}
            {msg.role === "assistant" &&
              msg.suggestedQuestions &&
              msg.suggestedQuestions.length > 0 &&
              i === chatHistory.length - 1 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginTop: "10px",
                  }}
                >
                  {msg.suggestedQuestions.map((q, qi) => (
                    <button
                      key={qi}
                      onClick={() => handleSearch(q)}
                      style={{
                        background: "rgba(255,77,141,0.08)",
                        border: "1px solid rgba(255,77,141,0.2)",
                        borderRadius: "16px",
                        padding: "6px 14px",
                        color: "var(--discovery-accent, #ff4d8d)",
                        fontSize: "0.78rem",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.background = "rgba(255,77,141,0.18)";
                        (e.target as HTMLElement).style.borderColor = "rgba(255,77,141,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.background = "rgba(255,77,141,0.08)";
                        (e.target as HTMLElement).style.borderColor = "rgba(255,77,141,0.2)";
                      }}
                    >
                      <i className="bx bx-right-arrow-alt" style={{ fontSize: "0.85rem" }}></i>
                      {q}
                    </button>
                  ))}
                </div>
              )}
          </div>
        ))}

        {isLoading && (
          <div className="chat-message assistant" data-testid="brain-loading-indicator">
            {/* Status indicator */}
            {(hideAiDetails || (statusMessage && !streamingText)) && (
              <div
                className="message-content"
                data-testid="brain-status-message"
                style={{
                  opacity: 0.6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: streamingText && !hideAiDetails ? "8px" : 0,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="bx bx-loader-alt bx-spin"></i>
                  {hideAiDetails ? rotatingMessages[rotatingMessageIndex] : statusMessage}
                </span>
                <button
                  onClick={handleCancel}
                  style={{
                    background: "rgba(255,77,141,0.12)",
                    border: "1px solid rgba(255,77,141,0.3)",
                    borderRadius: "6px",
                    padding: "4px 12px",
                    color: "var(--discovery-accent, #ff4d8d)",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = "rgba(255,77,141,0.25)";
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = "rgba(255,77,141,0.12)";
                  }}
                >
                  <i className="bx bx-stop" style={{ fontSize: "0.9rem" }}></i>
                  Stop
                </button>
              </div>
            )}
            {/* Streaming text */}
            {streamingText && !hideAiDetails && (
              <div
                className="message-content"
                data-testid="brain-streaming-text"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {streamingText.split("\n").map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < streamingText.split("\n").length - 1 && <br />}
                  </React.Fragment>
                ))}
                <span
                  className="streaming-cursor"
                  style={{
                    display: "inline-block",
                    width: "2px",
                    height: "1em",
                    background: "var(--discovery-accent, #ff4d8d)",
                    marginLeft: "2px",
                    animation: "blink 1s step-end infinite",
                    verticalAlign: "text-bottom",
                  }}
                />
                <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={handleCancel}
                    style={{
                      background: "rgba(255,77,141,0.12)",
                      border: "1px solid rgba(255,77,141,0.3)",
                      borderRadius: "6px",
                      padding: "4px 12px",
                      color: "var(--discovery-accent, #ff4d8d)",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <i className="bx bx-stop" style={{ fontSize: "0.9rem" }}></i>
                    Stop
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div
        className="chat-input-area"
        data-testid="brain-input-area"
        style={{ padding: "20px", borderTop: "1px solid #333" }}
      >
        {/* Global Filters Info */}
        <div style={{ marginBottom: "10px", fontSize: "0.8rem", color: "#666" }}>
          {activeFilters?.rundownIds?.length > 0 ||
          activeFilters?.eventIds?.length > 0 ||
          activeFilters?.storyIds?.length > 0 ||
          activeFilters?.participantIds?.length > 0 ||
          activeFilters?.tags?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                <i className="bx bx-filter-alt" style={{ color: "var(--discovery-accent)" }}></i>
                <span>Active Filters:</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginLeft: "20px" }}>
                {activeFilters.participantNames?.map((name: string, i: number) => (
                  <span key={`p-${i}`} className="filter-tag participant active">
                    {name}
                  </span>
                ))}
                {activeFilters.tagNames?.map((tag: string, i: number) => {
                  const isCamera = activeFilters.tagTypes?.[i] === "camera";
                  return (
                    <span
                      key={`t-${i}`}
                      className={`filter-tag ${isCamera ? "camera" : "tag"} active`}
                    >
                      {tag}
                    </span>
                  );
                })}
                {/* Rundowns */}
                {activeFilters.rundownNames?.map((name: string, i: number) => (
                  <span key={`r-${i}`} className="filter-tag rundown active">
                    {name}
                  </span>
                ))}
                {/* Events */}
                {activeFilters.eventNames?.map((name: string, i: number) => (
                  <span key={`e-${i}`} className="filter-tag event active">
                    {name}
                  </span>
                ))}
                {/* Stories */}
                {activeFilters.storyNames?.map((name: string, i: number) => (
                  <span key={`s-${i}`} className="filter-tag story active">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "5px", alignItems: "center", opacity: 0.5 }}>
              <i className="bx bx-filter-alt"></i>
              <span>
                {activeFilters?.selectAll
                  ? t("discovery.fullProjectSelected")
                  : t("discovery.noSelection")}
              </span>
            </div>
          )}
        </div>

        <div className="input-wrapper" style={{ position: "relative" }}>
          <textarea
            data-testid="brain-query-input"
            placeholder={
              disabled
                ? t("discovery.noSelection")
                : mode === "vtr"
                  ? "Pregunta sobre los VTRs / cortes del proyecto..."
                  : t("brain.placeholder")
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            rows={1}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto"; // Reset height
              target.style.height = `${Math.min(target.scrollHeight, 100)}px`; // Set to scrollHeight up to max ~100px
            }}
            style={{
              width: "100%",
              background: "var(--discovery-card-bg)",
              border: "1px solid var(--discovery-border)",
              padding: "12px 100px 12px 16px", // Padding right to avoid text going behind buttons
              borderRadius: "8px",
              color: disabled ? "#555" : "var(--discovery-text)",
              fontSize: "1rem",
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? "not-allowed" : "text",
              resize: "none",
              outline: "none",
              maxHeight: "100px",
              overflowY: "auto",
              fontFamily: "inherit",
              lineHeight: "1.5",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "8px",
              bottom: "10px",
              display: "flex",
              gap: "4px",
              alignItems: "center",
            }}
          >
            {/* Microphone button */}
            <button
              data-testid="brain-mic-btn"
              onClick={toggleListening}
              title={isListening ? "Stop dictation" : "Voice input"}
              style={{
                background: isListening ? "rgba(255,77,141,0.2)" : "rgba(255,255,255,0.05)",
                border: isListening ? "1px solid var(--discovery-accent)" : "1px solid transparent",
                color: isListening ? "var(--discovery-accent)" : "#888",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                animation: isListening ? "pulse-mic 1.5s ease-in-out infinite" : "none",
              }}
            >
              <i
                className={isListening ? "bx bx-stop" : "bx bx-microphone"}
                style={{ fontSize: "1.1rem" }}
              ></i>
            </button>
            {/* Send button */}
            <button
              data-testid="brain-send-btn"
              onClick={() => !disabled && handleSearch()}
              disabled={disabled || query.trim() === ""}
              style={{
                background: disabled || query.trim() === "" ? "#444" : "var(--discovery-accent)",
                border: "none",
                color: disabled || query.trim() === "" ? "#666" : "#fff",
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                cursor: disabled || query.trim() === "" ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bx bx-send"></i>
            </button>
          </div>
        </div>
      </div>
      <CustomDialog
        isOpen={!!globalAlert?.isOpen}
        type="alert"
        title={globalAlert?.title || t("discovery.attention")}
        message={globalAlert?.message || ""}
        onConfirm={() => setGlobalAlert(null)}
        onCancel={() => setGlobalAlert(null)}
      />
    </div>
  );
};
